import type { MachineLoadState, NeedsYouItem, Session } from '@orgus/protocol';
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { agentKindLabel, agentKindOf, ownWorkLine, ownWorkOf, sessionAskOf } from '@orgus/protocol';
import { sessionOutcome } from './outcome.ts';
import { AgentOrb, Badge, Button, Checkbox, Pressable, RelativeTime, Tooltip, cn, formatCompact, formatInt, formatMoney, plural } from '@orgus/ui';
import { AgentGlyph, agentHue } from '../adapters/AgentKind.tsx';
import type { AgentOrbState, WorkState } from '@orgus/ui';

/** A row's work state as the orb's ring. */
const ORB: Record<WorkState, AgentOrbState> = { 'needs-you': 'needs-you', failed: 'failed', running: 'working', verifying: 'working', 'waiting-for-cpu': 'idle', queued: 'idle', landed: 'idle', stale: 'idle' };
import { livenessGuessNote, sessionRowStatusOf } from '../adapters/state.ts';
import { tokenTotal } from './labels.ts';
import { SESSION_GROUP_META, sessionGroupOf } from './groups.ts';
import { failureLine, failureOf } from './failures.ts';
import { PARTIAL_HINT, compactLowerBound, subagentTotal, workflowTotal } from './counts.ts';
import { useSessionCost } from '../costs/useCosts.ts';
import { plainText } from '../../lib/plainText.ts';
import { useExt } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { findWaitingInfo } from '../machine-load/cpuWaiting.ts';
import { WaitingForCpuBadge } from '../machine-load/WaitingForCpuBadge.tsx';
import { MissionChip, useSessionTitle } from './title.tsx';
import { ApprovalBlockMark, useSessionBlock } from '../approvals/SessionBlockedBadge.tsx';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { rowStatusForLevel } from '../asks/levels.ts';

/**
 * Which optional facts the rows carry. A value the same on every visible row is not repeated
 * (spacing-layout.md, Rows): the list says it once above the rows instead.
 */
export interface SessionColumns {
  /** Rows name their project: more than one project is shown. */
  project: boolean;
  /** Rows name their machine: more than one machine is shown. */
  machine: boolean;
}

/** Numeric column widths, shared by the column header and every row so the columns line up. */
export const COL = {
  agents: 'w-14',
  tokens: 'w-16',
  cost: 'w-20',
  active: 'w-16 sm:w-20',
} as const;

/** The running workflow, or how many there were: the row's one chip. */
function WorkflowChip({ s }: { s: Session }) {
  const total = workflowTotal(s);
  if (!total) return null;
  const running = s.workflows.filter((w) => w.status === 'running').sort((a, b) => b.startedAt - a.startedAt);
  const w = running[0];
  if (!w) return null;
  const phases = w.phases.length;
  const done = w.phases.filter((p) => p.done >= p.agents).length;
  const more = running.length - 1;
  const text = `${w.name}${phases ? ` ${done}/${phases}` : ''}${more > 0 ? ` +${more}` : ''}`;
  return (
    <Tooltip content={`${w.description || w.name}: ${done} of ${plural(phases, 'phase')} done${more > 0 ? `, and ${plural(more, 'more workflow')} running` : ''}`}>
      <Badge data-chip="" tone="neutral" className="hidden min-w-0 max-w-48 shrink md:inline-flex" tabIndex={-1}>
        <span className="truncate num">{text}</span>
      </Badge>
    </Tooltip>
  );
}

/** Subagents: a number, "2/24" while some run. */
function AgentsCell({ s }: { s: Session }) {
  const total = subagentTotal(s);
  // QA-10: none reads as a dash, not a blank cell.
  if (!total) return <span className="text-fg-subtle" aria-label="No subagents">-</span>;
  const running = s.subagents.filter((a) => a.status === 'running').length;
  return (
    <Tooltip content={running ? `${formatInt(running)} running of ${formatInt(total)} started` : `${plural(total, 'subagent')} started`}>
      <span tabIndex={-1} className={cn(running && 'text-status-working')}>
        {running ? `${formatInt(running)}/${formatInt(total)}` : formatInt(total)}
      </span>
    </Tooltip>
  );
}

function CostCell({ id }: { id: string }) {
  const u = useSessionCost(id);
  if (!u || u.total.cost <= 0) return null;
  return <span title={u.source === 'notional' ? 'Notional: tokens at API list price' : 'At API list price'}>{formatMoney(u.total.cost)}</span>;
}

/** A soft accent wash for 600 ms when the row's status changes (status.md, Live changes). */
function useHighlight(key: string) {
  const ref = useRef<HTMLLIElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const el = ref.current;
    if (!el || typeof el.animate !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ms = Number.parseFloat(getComputedStyle(el).getPropertyValue('--duration-highlight')) || 600;
    el.animate([{ backgroundColor: 'var(--accent-soft)' }, { backgroundColor: 'transparent' }], { duration: ms, easing: 'ease-out' });
  }, [key]);
  return ref;
}

const cell = 'shrink-0 truncate text-right text-ui text-fg-muted num';
/** The cells a held row's answer button covers on hover or focus: cost and active time. */
const makeRoom = 'pointer-fine:group-hover/srow:invisible pointer-fine:group-focus-within/srow:invisible';

/** What a session that needs you is held on, in one line, and the action that answers it. */
export function needsFact(item: Pick<NeedsYouItem, 'kind' | 'reason' | 'session'> | undefined): { what: string; action: string } {
  if (item && item.kind !== 'session' && item.reason) return { what: item.reason, action: 'Review request' };
  // A question the session asked is what it waits on; its last progress note is not.
  const question = item?.session ? sessionAskOf(item.session.ext)?.question?.replace(/\s+/g, ' ').trim() : '';
  if (question) return { what: question, action: 'Answer question' };
  // Held with no request Orgus can read: its turn ended at a prompt.
  return { what: 'Needs your reply', action: 'Write reply' };
}

/**
 * One session, one line (spacing-layout.md, Rows): its state icon (the group says the word),
 * the title, at most two quiet facts (where it runs, then what it asks when it needs you or its
 * branch), at most one chip, then right-aligned numbers.
 */
export function SessionRow({
  s,
  branch,
  machineName,
  projectName,
  cols,
  selected,
  onOpen,
  style,
  checked = false,
  anyChecked = false,
  onCheck,
  className,
}: {
  s: Session;
  /** Shown when it is not the project's default branch. */
  branch?: string | null;
  machineName: string;
  projectName: string | null;
  cols: SessionColumns;
  selected: boolean;
  onOpen: () => void;
  /** Placement from a windowed list (VirtualGroups). */
  style?: CSSProperties;
  /** Bulk selection: this row is picked; some row is (the boxes then stay visible). */
  checked?: boolean;
  anyChecked?: boolean;
  /** Pick or unpick this row; `range` when Shift was held. Without it the row has no box. */
  onCheck?: (id: string, range: boolean) => void;
  /** Placement extras, such as the group indent step on a windowed row. */
  className?: string;
}) {
  const ref = useHighlight(s.status);
  // Waiting for CPU (ORGU-131): the machine's work queue names this session, so the row says so.
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const cpuInfo = findWaitingInfo(loadSlice, { sessionId: s.id, what: s.lastText });
  // Blocked reads Needs you; a turn that ended on a question reads Asked, quietly (ADR 0056).
  const needs = useNeedsYouState();
  const level = needs.bySession.get(s.id) ?? null;
  const st = rowStatusForLevel(s.status, level, sessionRowStatusOf(s));
  const group = SESSION_GROUP_META[sessionGroupOf(s, level)];
  const asksQuietly = s.status === 'waiting' && (level === 'asked' || level === 'open');
  const guess = livenessGuessNote(s);
  // Claude Code is the default and goes unsaid; any other agent shows in the orb's own glyph and
  // its tooltip, never as a third meta fact (row budget: two).
  const otherAgent = agentKindOf(s) !== 'claude-code';
  const waiting = s.status === 'waiting';
  const ended = s.status === 'ended';
  // A blocked session says what it is held on and how to answer it (no action-less Needs you).
  const held = waiting && level === 'blocked' ? needsFact(needs.blocked.find((i) => i.sessionId === s.id)) : null;
  const ask = held ? held.what : waiting && s.lastText ? plainText(s.lastText) : '';
  const where = [cols.project ? projectName : null, cols.machine ? machineName : null].filter(Boolean).join(' on ');
  // A failed session says why in its row (ADR 0059): the cause in one short clause, not the branch.
  const failure = ended ? failureOf(s) : null;
  // A turn that ended with its own work out names that work (ORGU-132).
  const own = ownWorkOf(s);
  const second = waiting ? ask : failure ? failureLine(failure) : own ? ownWorkLine(own, Date.now()) : branch || '';
  const total = tokenTotal(s);
  const shown = useSessionTitle()(s);
  const workflowChip = s.workflows.some((w) => w.status === 'running');
  // A decided approval that stopped the session is the row's one status: it takes the orb's place.
  const block = useSessionBlock(s);
  const stopped = block && block.phase !== 'resuming' ? block : null;
  const statusLabel = stopped ? stopped.label : block ? 'Resuming' : st.label;
  const tip = [guess ?? statusLabel, otherAgent ? agentKindLabel(s.agentKind) : ''].filter(Boolean).join(', ');
  const attention = waiting && !asksQuietly && level !== 'set-aside';
  return (
    <li ref={ref} className={cn('group/srow motion-arrive relative rounded-control', className)} style={style}>
      <Pressable
        data-session-row={s.id}
        data-row=""
        data-agents={subagentTotal(s)}
        aria-current={selected || undefined}
        onClick={onOpen}
        className={cn(
          'flex h-row items-center gap-2 rounded-control px-row-x md:gap-3 transition-colors duration-(--duration-fast)',
          selected ? 'selected-tint' : checked ? 'bg-accent-soft' : 'hover:bg-hover',
        )}
      >
        <span
          className={cn(
            'flex w-5 shrink-0 justify-center transition-opacity duration-(--duration-fast)',
            // The box takes the icon's place while it shows, so the two never draw over each other.
            onCheck && (anyChecked ? 'opacity-0' : 'pointer-fine:group-hover/srow:opacity-0 pointer-fine:group-focus-within/srow:opacity-0'),
          )}
          data-column="status"
          title={tip}
        >
          {stopped ? (
            <ApprovalBlockMark block={stopped} />
          ) : (
            // The agent's own glyph inside a ring that tells its state (AgentOrb).
            <AgentOrb state={block ? 'working' : asksQuietly ? 'asked' : ended ? (sessionOutcome(s) === 'failed' ? 'failed' : 'finished') : s.status === 'idle' ? 'idle' : ORB[group.state]} size={20} hue={agentHue(s.agentKind)} icon={<AgentGlyph kind={s.agentKind} />} label={statusLabel} />
          )}
          <span className="sr-only">{statusLabel}</span>
        </span>
        {/*
          The title gives way, never the chip: the title truncates, the chip keeps its own width, and
          nothing here can run under the numbers (the 390 px overlap in the live audit).
        */}
        <span className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
          {/* On a phone the title takes the whole line and the active time sits under it as a caption. */}
          <span className={cn('flex min-w-0 shrink flex-col max-md:flex-1 sm:min-w-24', waiting && 'md:max-w-1/2')}>
            <span className={cn('truncate text-ui font-medium', ended ? 'text-fg-muted' : 'text-fg')} title={shown.title}>
              {shown.title}
            </span>
            <span className={cn('truncate text-caption md:hidden', attention ? 'text-status-attention' : 'text-fg-subtle')}>
              <RelativeTime ts={s.lastActivity} />
            </span>
          </span>
          {stopped && (
            // The mark's label beside the title: status is icon plus label (the mark sits in the status column).
            <span data-chip="" className="shrink-0 text-caption font-medium text-status-danger" title={stopped.label}>
              Blocked
            </span>
          )}
          {shown.missionId && !workflowChip && !stopped && <MissionChip className="hidden shrink-0 md:inline-flex" />}
          {where && (
            <span data-meta="" className="hidden max-w-48 shrink-0 truncate text-caption text-fg-subtle lg:block" title={where}>
              {where}
            </span>
          )}
          {cpuInfo?.isWaiting ? (
            <span className="hidden min-w-16 max-w-48 shrink truncate text-caption sm:block">
              <WaitingForCpuBadge placeInLine={cpuInfo.placeInLine} estimateMinutes={cpuInfo.estimateMinutes} work={cpuInfo.work} />
            </span>
          ) : (
            second && (
              <span data-meta="" title={second} className={cn('hidden truncate text-caption sm:block', waiting ? 'min-w-24 flex-1 text-fg-muted' : failure ? 'min-w-24 flex-1 text-fg-subtle' : 'min-w-16 max-w-48 shrink text-fg-subtle')}>
                {second}
              </span>
            )
          )}
          <WorkflowChip s={s} />
        </span>
        <span data-column="agents" className={cn(COL.agents, cell, 'hidden md:block')}>
          <AgentsCell s={s} />
        </span>
        <span data-column="tokens" className={cn(COL.tokens, cell, 'hidden md:block')} title={s.partial ? PARTIAL_HINT : `${formatInt(s.tokens.input)} in, ${formatInt(s.tokens.output)} out, ${formatCompact(s.tokens.cacheRead)} cache reads`}>
          {total > 0 ? compactLowerBound(total, s.partial) : ''}
        </span>
        <span data-column="cost" className={cn(COL.cost, cell, 'hidden md:block', held && makeRoom)}>
          <CostCell id={s.id} />
        </span>
        <span data-column="active" className={cn(COL.active, cell, 'hidden text-caption md:block', attention ? 'text-status-attention' : 'text-fg-subtle', held && makeRoom)}>
          <RelativeTime ts={s.lastActivity} />
        </span>
      </Pressable>
      {held && (
        // The way to answer it, on hover or focus, over the active time (after the row button, so it sits on top).
        <span className="absolute inset-y-0 right-row-x hidden items-center opacity-0 transition-opacity duration-(--duration-fast) pointer-fine:md:flex pointer-fine:group-hover/srow:opacity-100 pointer-fine:group-focus-within/srow:opacity-100">
          <Button size="sm" tabIndex={-1} onClick={onOpen}>
            {held.action}
          </Button>
        </span>
      )}
      {onCheck && (
        // After the row button, so it sits on top of it, over the state icon: it shows on hover or focus, and stays while anything is picked.
        <span className={cn('absolute inset-y-0 left-row-x flex items-center', !anyChecked && 'pointer-fine:opacity-0 pointer-fine:group-hover/srow:opacity-100 pointer-fine:group-focus-within/srow:opacity-100')}>
          <Checkbox
            aria-label={`Select ${shown.title}`}
            checked={checked}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(_, e) => onCheck(s.id, (e.nativeEvent as MouseEvent).shiftKey)}
          />
        </span>
      )}
    </li>
  );
}
